import { SyntheticEvent, useEffect, useState } from 'react'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Checkbox from '@mui/material/Checkbox'
import CircularProgress from '@mui/material/CircularProgress'
import FormControl from '@mui/material/FormControl'
import FormControlLabel from '@mui/material/FormControlLabel'
import Grid from '@mui/material/Grid'
import InputLabel from '@mui/material/InputLabel'
import MenuItem from '@mui/material/MenuItem'
import Select from '@mui/material/Select'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'

import { ApplianceVariant, type ApplianceCodecCapacity } from 'common/api/v1/types'
import { ApplianceLicenseProduct, ApplianceLicenseProductFamily } from 'common-billing-server/types'
import {
    applianceLicenseMetricByFamily,
    applianceLicenseProductCodePattern,
    formatApplianceLicenseComponents,
    licensableApplianceModels,
} from 'common/applianceLicense'
import {
    formatApplianceCodecCapacity,
    HD_CHANNELS_PER_UHD,
    validateApplianceCodecCapacity,
} from 'common/applianceCodecLicense'
import { useBillingApi } from '../../api/billing/billing-context'
import { useFailedOperationBanner } from '../common/hooks/hook-failed-operation'

const licenseFamilies: ApplianceLicenseProductFamily[] = [
    ...(Object.keys(applianceLicenseMetricByFamily) as ApplianceLicenseProductFamily[]),
    'flow',
]

/**
 * Mirrors the server's capacity rule so a rejected pool is explained in the form instead of being
 * dropped when the request is never sent.
 */
const codecCapacityError = (capacity: ApplianceCodecCapacity | undefined): string | undefined => {
    if (!capacity) return undefined
    if (capacity.uhdChannels * HD_CHANNELS_PER_UHD > capacity.hdChannels)
        return `${capacity.uhdChannels} UHD needs at least ${capacity.uhdChannels * HD_CHANNELS_PER_UHD} HD capacity, since each UHD channel uses ${HD_CHANNELS_PER_UHD}`
    try {
        validateApplianceCodecCapacity(capacity)
    } catch (error) {
        return error instanceof Error ? error.message : 'Invalid codec capacity'
    }
    return undefined
}

/** Super-user management of license products, codec capacity, model restrictions and flow tiers. */
export const ApplianceLicenseProducts = () => {
    const billingApi = useBillingApi()
    const [products, setProducts] = useState<ApplianceLicenseProduct[]>([])
    const [isLoading, setIsLoading] = useState(true)
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [code, setCode] = useState('')
    const [name, setName] = useState('')
    const [licenseFamily, setLicenseFamily] = useState<ApplianceLicenseProductFamily>('encode_decode')
    const [entitlementValue, setEntitlementValue] = useState(1)
    const [unlimitedFlow, setUnlimitedFlow] = useState(false)
    const [codec, setCodec] = useState<'avc' | 'avcHevc'>('avc')
    const [uhdChannels, setUhdChannels] = useState(0)
    const [compatibleModels, setCompatibleModels] = useState<ApplianceVariant[]>([])
    const { addFailedOperation, removeFailedOperation, errorBanner } = useFailedOperationBanner()

    // Bundles come from NetSuite item groups, so the form never offers that family.
    const isFeatureFlag =
        licenseFamily !== 'flow' &&
        licenseFamily !== 'bundle' &&
        applianceLicenseMetricByFamily[licenseFamily] === 'featureFlag'
    const isUnlimitedFlow = licenseFamily === 'flow' && unlimitedFlow
    const effectiveValue = isFeatureFlag || isUnlimitedFlow ? 1 : entitlementValue
    const codecCapacity: ApplianceCodecCapacity | undefined =
        licenseFamily === 'encode_decode'
            ? {
                  codecs: codec === 'avc' ? ['h264'] : ['h264', 'h265'],
                  hdChannels: effectiveValue,
                  uhdChannels,
              }
            : undefined
    const capacityError = codecCapacityError(codecCapacity)

    const refreshProducts = async () => {
        const operationId = 'fetch-license-products'
        removeFailedOperation(operationId)
        setIsLoading(true)
        try {
            const result = await billingApi.listApplianceLicenseProducts()
            setProducts(result.items)
        } catch (error) {
            addFailedOperation({ id: operationId, message: 'Failed fetching appliance license products', error })
        }
        setIsLoading(false)
    }

    useEffect(() => {
        void refreshProducts()
        // oxlint-disable-next-line react-hooks/exhaustive-deps
    }, [billingApi])

    const createProduct = async (event: SyntheticEvent) => {
        event.preventDefault()
        const operationId = 'create-license-product'
        removeFailedOperation(operationId)
        setIsSubmitting(true)
        try {
            await billingApi.createApplianceLicenseProduct({
                code: code.trim(),
                name: name.trim(),
                licenseFamily,
                entitlementValue: effectiveValue,
                unlimited: isUnlimitedFlow,
                ...(codecCapacity ? { codecCapacity } : {}),
                ...(compatibleModels.length ? { compatibleModels } : {}),
            })
            setCode('')
            setName('')
            setUnlimitedFlow(false)
            setCompatibleModels([])
            await refreshProducts()
        } catch (error) {
            addFailedOperation({ id: operationId, message: `Failed creating product "${code}"`, error })
        }
        setIsSubmitting(false)
    }

    return (
        <Box sx={{ p: 2 }}>
            {errorBanner}
            {isLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                    <CircularProgress />
                </Box>
            ) : (
                <TableContainer>
                    <Table size="small" sx={{ minWidth: 720 }} data-testid="license-product-table">
                        <TableHead>
                            <TableRow>
                                <TableCell>Code</TableCell>
                                <TableCell>Name</TableCell>
                                <TableCell>Family</TableCell>
                                <TableCell>Metric</TableCell>
                                <TableCell>Models</TableCell>
                                <TableCell align="right">Licensed capacity</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {products.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={6}>
                                        <Typography variant="body2" color="text.secondary">
                                            No license products defined yet. Products created here become available for
                                            purchase on the customer pages.
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            )}
                            {products.map((product) => (
                                <TableRow key={product.code}>
                                    <TableCell>{product.code}</TableCell>
                                    <TableCell>{product.name}</TableCell>
                                    <TableCell>{product.licenseFamily}</TableCell>
                                    <TableCell>{product.entitlementMetric}</TableCell>
                                    <TableCell>{product.compatibleModels?.join(', ') ?? 'All'}</TableCell>
                                    <TableCell align="right">
                                        {product.components
                                            ? formatApplianceLicenseComponents(product.components).join(', ')
                                            : product.codecCapacity
                                              ? formatApplianceCodecCapacity(product.codecCapacity)
                                              : product.entitlementMetric === 'unlimitedFlows'
                                                ? 'Max'
                                                : product.entitlementValue}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            <Typography variant="h6" sx={{ mt: 3, mb: 1 }}>
                New product
            </Typography>
            <form onSubmit={(e) => void createProduct(e)}>
                <Grid container spacing={2} sx={{ alignItems: 'flex-start' }}>
                    <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                        <TextField
                            fullWidth
                            required
                            label="Code"
                            placeholder="NPM0101-M4H1"
                            value={code}
                            onChange={(e) => setCode(e.target.value.toUpperCase())}
                            slotProps={{
                                htmlInput: {
                                    pattern: applianceLicenseProductCodePattern,
                                    'data-testid': 'product-code',
                                },
                            }}
                        />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                        <TextField
                            fullWidth
                            required
                            label="Name"
                            placeholder="MPEG-4 HD Encode/Decode - 1x HD codec"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                        />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                        <FormControl fullWidth>
                            <InputLabel id="license-family-label">Family</InputLabel>
                            <Select
                                labelId="license-family-label"
                                label="Family"
                                value={licenseFamily}
                                onChange={(e) => {
                                    const family = e.target.value as ApplianceLicenseProductFamily
                                    setLicenseFamily(family)
                                    if (family !== 'flow') setUnlimitedFlow(false)
                                    else setCompatibleModels([])
                                }}
                            >
                                {licenseFamilies.map((family) => (
                                    <MenuItem key={family} value={family}>
                                        {family}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                        <TextField
                            fullWidth
                            type="number"
                            label={
                                isFeatureFlag
                                    ? 'Value (feature flag)'
                                    : licenseFamily === 'flow'
                                      ? 'Flows'
                                      : 'HD capacity'
                            }
                            value={effectiveValue}
                            disabled={isFeatureFlag || isUnlimitedFlow}
                            onChange={(e) => setEntitlementValue(Math.max(1, parseInt(e.target.value, 10) || 1))}
                            slotProps={{ htmlInput: { min: 1 } }}
                        />
                        {licenseFamily === 'flow' ? (
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={unlimitedFlow}
                                        onChange={(e) => setUnlimitedFlow(e.target.checked)}
                                    />
                                }
                                label="Maximum"
                            />
                        ) : null}
                    </Grid>
                    {licenseFamily === 'encode_decode' && (
                        <>
                            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                                <TextField
                                    select
                                    fullWidth
                                    label="Licensed video codecs"
                                    value={codec}
                                    onChange={(e) => setCodec(e.target.value as typeof codec)}
                                >
                                    <MenuItem value="avc">AVC/H.264 only</MenuItem>
                                    <MenuItem value="avcHevc">AVC/H.264 + HEVC/H.265</MenuItem>
                                </TextField>
                            </Grid>
                            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                                <TextField
                                    fullWidth
                                    type="number"
                                    label="Maximum UHD channels (0 = HD only)"
                                    value={uhdChannels}
                                    error={!!capacityError}
                                    helperText={
                                        capacityError ??
                                        `HD and UHD can be mixed. Each UHD uses ${HD_CHANNELS_PER_UHD} HD units; encode and decode share the total.`
                                    }
                                    onChange={(e) => setUhdChannels(Math.max(0, parseInt(e.target.value, 10) || 0))}
                                    slotProps={{ htmlInput: { min: 0, 'data-testid': 'uhd-channels' } }}
                                />
                            </Grid>
                        </>
                    )}
                    {licenseFamily !== 'flow' && (
                        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                            <TextField
                                select
                                fullWidth
                                label="Compatible models"
                                value={compatibleModels}
                                helperText="Leave empty to allow every model. A license is rejected on any other model."
                                onChange={(e) =>
                                    setCompatibleModels(
                                        (typeof e.target.value === 'string'
                                            ? e.target.value.split(',')
                                            : e.target.value) as ApplianceVariant[]
                                    )
                                }
                                slotProps={{ select: { multiple: true } }}
                            >
                                {licensableApplianceModels.map((model) => (
                                    <MenuItem key={model} value={model}>
                                        {model}
                                    </MenuItem>
                                ))}
                            </TextField>
                        </Grid>
                    )}
                    <Grid size={{ xs: 12 }}>
                        <Button
                            type="submit"
                            variant="contained"
                            disabled={isSubmitting || !code.trim() || !name.trim() || !!capacityError}
                            startIcon={isSubmitting ? <CircularProgress size={16} color="inherit" /> : undefined}
                        >
                            Create
                        </Button>
                    </Grid>
                </Grid>
            </form>
        </Box>
    )
}
